import { useMemo } from "react";

import clsx from "clsx";

import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { FlatSelect } from "@shared/flat-select";

const timePeriodOptionRender =
  (label: string) =>
  ({ isSelected }: { isSelected: boolean }) =>
    (
      <div
        className={clsx("px-4 flex-center h-full", {
          "text-white font-semibold": isSelected,
          "text-v2-primary font-normal": !isSelected,
        })}
      >
        {label}
      </div>
    );

interface Props {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const kpiPeriodValueToLabel = (value: unknown) => {
  const mapping = {
    last_4_weeks: "4WK",
    last_5_weeks: "5WK",
    last_26_weeks: "26WK",
    last_52_weeks: "52WK",
    last_year: "LAST YEAR",
    all_time: "ALL TIME",
  };

  if (!mapping[value as keyof typeof mapping]) {
    return "Unknown";
  }
  return mapping[value as keyof typeof mapping];
};

export const KpiPeriodSelector = ({ value, onChange, className }: Props) => {
  const { is4WeekCycle } = useSelectedCycle();

  const options = useMemo(
    () =>
      [
        is4WeekCycle
          ? {
              label: kpiPeriodValueToLabel("last_4_weeks"),
              value: "last_4_weeks",
            }
          : {
              label: kpiPeriodValueToLabel("last_5_weeks"),
              value: "last_5_weeks",
            },
        {
          label: kpiPeriodValueToLabel("last_26_weeks"),
          value: "last_26_weeks",
        },
        {
          label: kpiPeriodValueToLabel("last_52_weeks"),
          value: "last_52_weeks",
        },
        { label: kpiPeriodValueToLabel("last_year"), value: "last_year" },
        { label: kpiPeriodValueToLabel("all_time"), value: "all_time" },
      ].map((e) => ({
        ...e,
        renderOption: timePeriodOptionRender(e.label),
      })),
    [is4WeekCycle],
  );

  const selected = useMemo(() => {
    const option = options.find((e) => e.value === value);
    if (option) {
      return option;
    }
    return options[0];
  }, [value, options]);

  return (
    <div className={clsx(className)}>
      <div className="mb-1 text-xs">
        <span className="font-medium text-interstellar+2 mr-2 tracking-wide">
          TIME PERIOD
        </span>
      </div>
      <FlatSelect
        options={options}
        setSelected={(option) => onChange(option.value)}
        selected={selected}
      />
    </div>
  );
};
